import React from "react"
import { useState, useEffect } from "react"
import { X, ChevronDown, ChevronRight, User2, UserCheck } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Checkbox } from "../../../components/ui/checkbox"
import { Textarea } from "../../../components/ui/textarea"
import { ScrollArea } from "../../../components/ui/scroll-area"
import { toast } from "react-toastify"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "../../../components/ui/sheet"
import { usePermissionManagement } from "../../Services/PermissionManagement"

interface Permission {
    id: string
    key: string
    resource: string
    action: string
    description: string
}

interface PermissionCategory {
    id: string
    name: string
    description: string
    permissions: Permission[]
}

interface Role {
    id?: string
    roleName: string
    description: string
    permissionCategories: PermissionCategory[]
}

interface RoleFormData {
    roleName: string
    description: string
    permissionCategories: PermissionCategory[]
}

interface RoleCreationProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    permissionCategories?: PermissionCategory[]
    editingRole?: Role | null
    onRoleChange?: () => void
}

export default function RoleCreation({
    open,
    onOpenChange,
    permissionCategories = [],
    editingRole = null,
    onRoleChange
}: RoleCreationProps) {
    const [formData, setFormData] = useState<RoleFormData>({
        roleName: "",
        description: "",
        permissionCategories: [],
    })

    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
    const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set())
    const [searchQuery, setSearchQuery] = useState("")
    const { createRole, updateRole } = usePermissionManagement();
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (editingRole) {
            setFormData({
                roleName: editingRole.roleName,
                description: editingRole.description,
                permissionCategories: editingRole.permissionCategories,
            })

            const permIds = new Set<string>()
            editingRole.permissionCategories.forEach((cat) => {
                cat.permissions.forEach((perm) => {
                    permIds.add(perm.id)
                })
            })
            setSelectedPermissions(permIds)
            setExpandedCategories(new Set())
        } else {
            resetForm()
        }
    }, [editingRole, open])

    const handleRoleNameChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            roleName: value,
        }))
    }

    const handleDescriptionChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            description: value,
        }))
    }

    const toggleCategory = (categoryId: string) => {
        const newExpanded = new Set(expandedCategories)
        if (newExpanded.has(categoryId)) {
            newExpanded.delete(categoryId)
        } else {
            newExpanded.add(categoryId)
        }
        setExpandedCategories(newExpanded)
    }

    const togglePermission = (permissionId: string, categoryId: string) => {
        const newSelected = new Set(selectedPermissions)
        if (newSelected.has(permissionId)) {
            newSelected.delete(permissionId)
        } else {
            newSelected.add(permissionId)
        }
        setSelectedPermissions(newSelected)
    }

    const toggleCategoryPermissions = (categoryId: string) => {
        const category = permissionCategories.find((c) => c.id === categoryId)
        if (!category) return

        const newSelected = new Set(selectedPermissions)
        const allPermissionIds = category.permissions.map((p) => p.id)
        const allSelected = allPermissionIds.every((id) => newSelected.has(id))

        if (allSelected) {
            allPermissionIds.forEach((id) => newSelected.delete(id))
        } else {
            allPermissionIds.forEach((id) => newSelected.add(id))
        }

        setSelectedPermissions(newSelected)
    }

    const handleSubmit = async () => {
        if (!formData.roleName.trim()) {
            toast.error("Role name is required")
            return
        }

        if (selectedPermissions.size === 0) {
            toast.error("Please select at least one permission")
            return
        }

        setIsSubmitting(true)

        try {
            const selectedCategoriesWithPermissions = permissionCategories
                .map((category) => ({
                    ...category,
                    permissions: category.permissions.filter((p) => selectedPermissions.has(p.id)),
                }))
                .filter((category) => category.permissions.length > 0)

            const roleData: Role = {
                // id: editingRole?.id || `${Date.now()}-${Math.random()}`,
                roleName: formData.roleName,
                description: formData.description,
                permissionCategories: selectedCategoriesWithPermissions,
            }

            console.log(editingRole ? "Updating role:" : "Creating role:", roleData)


            if (editingRole) {
                await updateRole({ ...roleData, id: editingRole?.id });
                toast.success("Role updated successfully!")
            } else {
                await createRole(roleData);
                toast.success("Role created successfully!")
            }

            onRoleChange();
            onOpenChange(false)
            resetForm()
        } catch (error) {
            toast.error(editingRole ? "Failed to update role" : "Failed to create role")
            console.error("Error saving role:", error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const resetForm = () => {
        setFormData({
            roleName: "",
            description: "",
            permissionCategories: [],
        })
        setSelectedPermissions(new Set())
        setExpandedCategories(new Set())
        setSearchQuery("")
    }

    const handleClose = () => {
        onOpenChange(false)
        resetForm()
    }

    if (!open) return null

    const getCategorySelectedCount = (categoryId: string) => {
        const category = permissionCategories.find((c) => c.id === categoryId)
        if (!category) return 0
        return category.permissions.filter((p) => selectedPermissions.has(p.id)).length
    }

    const isCategoryFullySelected = (categoryId: string) => {
        const category = permissionCategories.find((c) => c.id === categoryId)
        if (!category) return false
        return category.permissions.length > 0 && category.permissions.every((p) => selectedPermissions.has(p.id))
    }

    const normalizedQuery = searchQuery.trim().toLowerCase()

    const filteredCategories = normalizedQuery
        ? permissionCategories
            .map((category) => {
                const categoryMatches =
                    category.name.toLowerCase().includes(normalizedQuery) ||
                    category.description?.toLowerCase().includes(normalizedQuery)

                const matchingPermissions = category.permissions.filter((permission) =>
                    permission.action.toLowerCase().includes(normalizedQuery) ||
                    permission.resource.toLowerCase().includes(normalizedQuery) ||
                    permission.key?.toLowerCase().includes(normalizedQuery) ||
                    permission.description?.toLowerCase().includes(normalizedQuery)
                )

                if (categoryMatches) {
                    return category
                }

                if (matchingPermissions.length > 0) {
                    return { ...category, permissions: matchingPermissions }
                }

                return null
            })
            .filter((category): category is PermissionCategory => category !== null)
        : permissionCategories

    const isCategoryExpanded = (categoryId: string) =>
        normalizedQuery ? true : expandedCategories.has(categoryId)

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto">
                    <SheetHeader className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-b detail-header">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="bg-purple-100 p-2 rounded-lg">
                                    <UserCheck className="h-6 w-6 text-purple-600" />
                                </div>
                                <div>
                                    <SheetTitle className="text-xl font-bold text-gray-900">{editingRole ? "Edit User Role" : "Create User Role"}</SheetTitle>
                                    <p className="text-sm text-gray-600 mt-1">{editingRole
                                        ? "Update role and manage permissions"
                                        : "Define a new role and assign permissions to manage access control"}</p>
                                </div>
                            </div>
                        </div>
                        <Button variant="outline" onClick={handleClose} className="close-panel">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-x h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                        </Button>
                    </SheetHeader>
                    <div className="space-y-6">
                        {/* Content */}
                        <ScrollArea className="flex-1 overflow-hidden h-[calc(100vh-200px)]">
                            <div className="p-6 space-y-6">
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="roleName" className="text-sm font-semibold text-white block mb-2">
                                            Role Name <span className="text-amber-500">*</span>
                                        </Label>
                                        <Input
                                            id="roleName"
                                            placeholder="e.g., Content Manager, Editor"
                                            value={formData.roleName}
                                            onChange={(e) => handleRoleNameChange(e.target.value)}
                                            className="border-slate-700 text-white placeholder:text-slate-600"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="description" className="text-sm font-semibold text-white block mb-2">
                                            Description
                                        </Label>
                                        <Textarea
                                            id="description"
                                            placeholder="Describe the purpose of this role..."
                                            value={formData.description}
                                            onChange={(e) => handleDescriptionChange(e.target.value)}
                                            className="border-slate-700 text-white placeholder:text-slate-600 resize-none"
                                            rows={3}
                                        />
                                    </div>
                                </div>

                                {/* Permissions Section */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-base font-semibold text-white">Assign Permissions</h3>
                                            <p className="text-xs text-slate-400 mt-1">Select permissions to grant to this role</p>
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <Input
                                            placeholder="Search permissions by category or action..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="border-slate-700 text-white placeholder:text-slate-600 pl-10"
                                        />
                                        <svg
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                    </div>

                                    {/* Permission Categories */}
                                    <div className="space-y-2">
                                        {filteredCategories.length > 0 ? (
                                            filteredCategories.map((category) => (
                                                <div
                                                    key={category.id}
                                                    className="border border-slate-700 rounded-lg overflow-hidden"
                                                >
                                                    {/* Category Header */}
                                                    <div
                                                        onClick={() => toggleCategory(category.id)}
                                                        className="flex items-center gap-3 p-4 hover:bg-gray-800 cursor-pointer transition-colors"
                                                    >
                                                        <div className="flex-shrink-0">
                                                            {isCategoryExpanded(category.id) ? (
                                                                <ChevronDown className="h-5 w-5 text-slate-400" />
                                                            ) : (
                                                                <ChevronRight className="h-5 w-5 text-slate-400" />
                                                            )}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-medium text-sm text-white">{category.name}</p>
                                                            {category.description && (
                                                                <p className="text-xs text-slate-400 mt-0.5">{category.description}</p>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-xs bg-amber-500/10 text-amber-500 px-2 py-1 rounded font-medium">
                                                                {getCategorySelectedCount(category.id)}/{category.permissions.length}
                                                            </span>
                                                            <Checkbox
                                                                checked={isCategoryFullySelected(category.id)}
                                                                onCheckedChange={() => toggleCategoryPermissions(category.id)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="border-slate-600"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Permissions List */}
                                                    {isCategoryExpanded(category.id) && (
                                                        <div className="p-4 space-y-3 border-t border-slate-700">
                                                            {category.permissions.map((permission) => (
                                                                <div
                                                                    key={permission.id}
                                                                    className="flex items-start gap-3 p-2 rounded hover:bg-gray-800 transition-colors"
                                                                >
                                                                    <Checkbox
                                                                        id={permission.id}
                                                                        checked={selectedPermissions.has(permission.id)}
                                                                        onCheckedChange={() => togglePermission(permission.id, category.id)}
                                                                        className="mt-1 border-slate-600"
                                                                    />
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <label
                                                                                htmlFor={permission.id}
                                                                                className="text-sm font-medium text-white cursor-pointer"
                                                                            >
                                                                                {permission.action}
                                                                            </label>
                                                                            <span className="text-xs bg-gray-800 text-slate-300 px-2 py-0.5 rounded font-mono">
                                                                                {permission.resource}
                                                                            </span>
                                                                        </div>
                                                                        {permission.description && (
                                                                            <p className="text-xs text-slate-400 mt-1">{permission.description}</p>
                                                                        )}
                                                                        {permission.key && (
                                                                            <p className="text-xs text-slate-500 font-mono mt-0.5">{permission.key}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-6 text-center border border-dashed border-slate-700 rounded-lg bg-gray-900/50">
                                                <p className="text-slate-400">No permissions available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </ScrollArea>

                        {/* Footer Actions */}
                        <div className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-3">
                            <Button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !formData.roleName.trim() || selectedPermissions.size === 0}
                                className="bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                                {isSubmitting ? (editingRole ? "Updating..." : "Creating...") : editingRole ? "Update Role" : "Create Role"}
                            </Button>
                        </div>
                        {/* <div className="flex gap-3 pt-4">
                                <Button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || !formData.roleName.trim() || selectedPermissions.size === 0}
                                    className="flex-1 bg-primary hover:bg-primary-300"
                                >
                                    {isSubmitting ? (editingRole ? "Updating..." : "Creating...") : editingRole ? "Update Role" : "Create Role"}
                                </Button>
                            </div> */}
                    </div>
                </SheetContent>
            </Sheet>

            {/* <div className="fixed inset-0 bg-black/70 z-40 transition-opacity" onClick={handleClose} /> */}

            {/* <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl border-l border-slate-800 shadow-2xl z-50 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-slate-800 bg-gray-950">
                    <div>
                        <h2 className="text-2xl font-bold text-white">{editingRole ? "Edit User Role" : "Create User Role"}</h2>
                        <p className="text-sm text-slate-400 mt-1">
                            {editingRole
                                ? "Update role and manage permissions"
                                : "Define a new role and assign permissions to manage access control"}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-slate-400 hover:text-white"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="border-t border-slate-800 p-6 flex gap-3 justify-end">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="border-slate-700 text-slate-300 hover:bg-gray-800 hover:text-white bg-transparent"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !formData.roleName.trim() || selectedPermissions.size === 0}
                        className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6"
                    >
                        {isSubmitting ? (editingRole ? "Updating..." : "Creating...") : editingRole ? "Update Role" : "Create Role"}
                    </Button>
                </div>
            </div> */}
        </>
    )
}
